import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import api from '../utils/api';
import { toast } from 'sonner';
import { ArrowLeft, Send, Mail, Users, FileText, Paperclip, TestTube, Eye, Loader2, Bold, Italic, Strikethrough, List, ListOrdered, Link as LinkIcon, Underline as UnderlineIcon } from 'lucide-react';

const NOTIFICATION_TYPES = [
  { value: 'news_updates', label: 'News & Updates', color: 'bg-blue-100 text-blue-700' },
  { value: 'service_announcements', label: 'Service Announcements', color: 'bg-purple-100 text-purple-700' },
  { value: 'maintenance', label: 'Maintenance Notifications', color: 'bg-orange-100 text-orange-700' },
  { value: 'marketing', label: 'Marketing/Promotions', color: 'bg-green-100 text-green-700' },
  { value: 'system_alerts', label: 'System Alerts', color: 'bg-red-100 text-red-700' }
];

const ComposeEmail = () => {
  const navigate = useNavigate();
  const [notificationType, setNotificationType] = useState('');
  const [subject, setSubject] = useState('');
  const [availableContacts, setAvailableContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [testEmail, setTestEmail] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Tiptap editor configuration
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({
        openOnClick: false,
      }),
      TextStyle,
      Color,
    ],
    content: '<p>Compose your email here...</p>',
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[300px] p-4',
      },
    },
  });

  useEffect(() => {
    if (notificationType) {
      fetchContactsByType(notificationType);
    }
  }, [notificationType]);

  const fetchContactsByType = async (type) => {
    setLoading(true);
    try {
      const response = await api.get(`/contacts/by-notification-type/${type}`);
      setAvailableContacts(response.data);
      // Auto-select all by default
      setSelectedContacts(response.data.map(c => c.id));
    } catch (error) {
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const toggleContact = (contactId) => {
    if (selectedContacts.includes(contactId)) {
      setSelectedContacts(selectedContacts.filter(id => id !== contactId));
    } else {
      setSelectedContacts([...selectedContacts, contactId]);
    }
  };

  const toggleAll = () => {
    if (selectedContacts.length === availableContacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(availableContacts.map(c => c.id));
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setAttachments([...attachments, ...files]);
  };

  const removeAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const handleAIAnalyze = async () => {
    if (!editor || editor.getText().trim().length === 0) {
      toast.error('Please enter some message content first');
      return;
    }

    if (!notificationType) {
      toast.error('Please select a notification type first');
      return;
    }

    setTesting(true);
    try {
      const plainText = editor.getText();

      const response = await api.post('/emails/ai-analyze', {
        body: plainText,
        notification_type: notificationType,
        subject: subject,
        return_html: true
      });

      editor.commands.setContent(response.data.improved_body);
      if (response.data.improved_subject) {
        setSubject(response.data.improved_subject);
      }
      toast.success('Email improved by AI!');
    } catch (error) {
      toast.error('Failed to analyze email');
    } finally {
      setTesting(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter a test email address');
      return;
    }

    if (!subject || !editor || editor.getText().trim().length === 0) {
      toast.error('Please fill in subject and body');
      return;
    }

    setTesting(true);
    try {
      const formData = new FormData();
      formData.append('subject', subject);
      formData.append('body', editor.getHTML());
      formData.append('recipient', testEmail);
      
      attachments.forEach((file, index) => {
        formData.append(`attachments`, file);
      });

      await api.post('/emails/test', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Test email sent!');
    } catch (error) {
      toast.error('Failed to send test email');
    } finally {
      setTesting(false);
    }
  };

  const handleSend = async () => {
    if (!notificationType) {
      toast.error('Please select a notification type');
      return;
    }

    if (selectedContacts.length === 0) {
      toast.error('Please select at least one recipient');
      return;
    }

    if (!subject || !editor || editor.getText().trim().length === 0) {
      toast.error('Please fill in subject and body');
      return;
    }

    setSending(true);
    try {
      const formData = new FormData();
      formData.append('notification_type', notificationType);
      formData.append('subject', subject);
      formData.append('body', editor.getHTML());
      formData.append('contact_ids', JSON.stringify(selectedContacts));
      
      attachments.forEach((file) => {
        formData.append(`attachments`, file);
      });

      const response = await api.post('/emails/send-bulk', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success(`Email sent to ${response.data.sent_count} recipients!`);
      navigate('/clients');
    } catch (error) {
      toast.error('Failed to send emails');
    } finally {
      setSending(false);
    }
  };

  const selectedContactDetails = availableContacts.filter(c => selectedContacts.includes(c.id));

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/clients')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Compose Email</h1>
            <p className="text-sm text-slate-600">Send bulk emails to selected contacts</p>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Composer */}
        <div className="lg:col-span-2 space-y-4">
          {/* Notification Type */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Email Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Notification Type *</Label>
                <Select value={notificationType} onValueChange={setNotificationType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select notification type" />
                  </SelectTrigger>
                  <SelectContent>
                    {NOTIFICATION_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  Only contacts with this notification type enabled will be shown
                </p>
              </div>

              <div>
                <Label>Subject *</Label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Enter email subject"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Message Body *</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAIAnalyze}
                    disabled={!editor || !notificationType || testing}
                    className="border-purple-300 text-purple-700 hover:bg-purple-50"
                  >
                    {testing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                          <path d="M2 17l10 5 10-5"/>
                          <path d="M2 12l10 5 10-5"/>
                        </svg>
                        AI Analyze
                      </>
                    )}
                  </Button>
                </div>
                
                {/* Tiptap Toolbar */}
                {editor && (
                  <div className="border border-slate-300 rounded-t-md bg-slate-50 p-2 flex flex-wrap gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => editor.chain().focus().toggleBold().run()}
                      className={editor.isActive('bold') ? 'bg-slate-200' : ''}
                    >
                      <Bold className="w-4 h-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => editor.chain().focus().toggleItalic().run()}
                      className={editor.isActive('italic') ? 'bg-slate-200' : ''}
                    >
                      <Italic className="w-4 h-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => editor.chain().focus().toggleUnderline().run()}
                      className={editor.isActive('underline') ? 'bg-slate-200' : ''}
                    >
                      <UnderlineIcon className="w-4 h-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => editor.chain().focus().toggleStrike().run()}
                      className={editor.isActive('strike') ? 'bg-slate-200' : ''}
                    >
                      <Strikethrough className="w-4 h-4" />
                    </Button>
                    <div className="w-px h-8 bg-slate-300 mx-1" />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => editor.chain().focus().toggleBulletList().run()}
                      className={editor.isActive('bulletList') ? 'bg-slate-200' : ''}
                    >
                      <List className="w-4 h-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => editor.chain().focus().toggleOrderedList().run()}
                      className={editor.isActive('orderedList') ? 'bg-slate-200' : ''}
                    >
                      <ListOrdered className="w-4 h-4" />
                    </Button>
                    <div className="w-px h-8 bg-slate-300 mx-1" />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                      className={editor.isActive('heading', { level: 2 }) ? 'bg-slate-200' : ''}
                    >
                      H2
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                      className={editor.isActive('heading', { level: 3 }) ? 'bg-slate-200' : ''}
                    >
                      H3
                    </Button>
                  </div>
                )}
                
                {/* Tiptap Editor */}
                <div className="border border-t-0 border-slate-300 rounded-b-md bg-white">
                  <EditorContent editor={editor} />
                </div>
                
                <p className="text-xs text-slate-500 mt-1">
                  Use the toolbar to format your email. AI Analyze will improve your message while preserving formatting.
                </p>
              </div>

              {/* Attachments */}
              <div>
                <Label>Attachments (Optional)</Label>
                <div className="mt-2">
                  <input
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                    id="file-upload"
                  />
                  <label htmlFor="file-upload">
                    <Button type="button" variant="outline" size="sm" asChild>
                      <span className="cursor-pointer">
                        <Paperclip className="w-4 h-4 mr-2" />
                        Add Files
                      </span>
                    </Button>
                  </label>
                  
                  {attachments.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {attachments.map((file, index) => (
                        <div key={index} className="flex items-center justify-between bg-slate-50 p-2 rounded">
                          <span className="text-sm text-slate-700 flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            {file.name} ({(file.size / 1024).toFixed(1)} KB)
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeAttachment(index)}
                            className="text-red-600 hover:text-red-700"
                          >
                            Remove
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Test Email Section */}
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 mb-3">
                <TestTube className="w-5 h-5 text-amber-600" />
                <h3 className="font-semibold text-amber-900">Send Test Email</h3>
              </div>
              <p className="text-sm text-amber-700 mb-3">
                Send a test to yourself before sending to all recipients
              </p>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="your-email@example.com"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  className="flex-1"
                />
                <Button
                  variant="outline"
                  onClick={handleTestEmail}
                  disabled={testing || !subject || !editor || editor.getText().trim().length === 0}
                  className="border-amber-300"
                >
                  {testing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Test
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recipients Sidebar */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Recipients
                </div>
                <Badge variant="outline">
                  {selectedContacts.length} selected
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!notificationType ? (
                <p className="text-sm text-slate-500 text-center py-4">
                  Select a notification type to load contacts
                </p>
              ) : loading ? (
                <p className="text-sm text-slate-500 text-center py-4">
                  Loading contacts...
                </p>
              ) : availableContacts.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">
                  No contacts found with this notification type enabled
                </p>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between pb-2 border-b">
                    <Checkbox
                      checked={selectedContacts.length === availableContacts.length}
                      onCheckedChange={toggleAll}
                    />
                    <span className="text-sm font-medium">Select All</span>
                  </div>
                  
                  <div className="space-y-2 max-h-[600px] overflow-y-auto">
                    {availableContacts.map(contact => (
                      <div
                        key={contact.id}
                        className="flex items-start gap-3 p-2 rounded hover:bg-slate-50 cursor-pointer"
                        onClick={() => toggleContact(contact.id)}
                      >
                        <Checkbox
                          checked={selectedContacts.includes(contact.id)}
                          onCheckedChange={() => toggleContact(contact.id)}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800">
                            {contact.first_name} {contact.last_name}
                          </p>
                          <p className="text-xs text-slate-500 truncate">
                            {contact.email}
                          </p>
                          <p className="text-xs text-slate-400">
                            {contact.company_name} - {contact.job_title}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Send Button */}
          <Button
            onClick={handleSend}
            disabled={sending || selectedContacts.length === 0 || !subject || !editor || editor.getText().trim().length === 0}
            className="w-full bg-blue-600 hover:bg-blue-700"
            size="lg"
          >
            {sending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Send to {selectedContacts.length} {selectedContacts.length === 1 ? 'Recipient' : 'Recipients'}
              </>
            )}
          </Button>

          {selectedContacts.length > 0 && (
            <p className="text-xs text-slate-500 text-center">
              All recipients will be BCC'd to protect their privacy
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComposeEmail;
